import { setup, fromPromise } from 'xstate';
import { ActorErrorParams } from '../../../../lib/types';
import {
  FraudAlloyMachineEvents,
  SendStartIDUploadJourneyParams,
} from './types/events';
import {
  InitializingFraudAlloyResult,
  StartIDUploadJourneyResult,
} from './types/actors';
import {
  getInitializeFraudAlloyParams,
  getStartIDUploadJourney,
  getServiceErrorParams,
  emitIDUploadJourneyFailure,
  emitIDUploadJourneySuccess,
  emitFraudAlloyMachineReady,
} from './actions';
import { assignFraudAlloyInitResponse } from './assigns';
import { getStartIDUploadJourneyInput } from './inputs';

export interface FraudAlloyContext {
  neuroIdSiteId: string | null;
  neuroUserId: string | null;
  iovationBlackboxId: string | null;
}

export const fraudAlloyMachine = setup({
  types: {
    context: {} as FraudAlloyContext,
    events: {} as FraudAlloyMachineEvents,
  },
  guards: {
    isIDUploadJourneySuccess: (_, __: StartIDUploadJourneyResult): boolean => {
      throw new Error('Not implemented');
    },
  },
  actions: {
    assignFraudAlloyInitResponse,
    emitFraudAlloyMachineReady,
    emitIDUploadJourneySuccess,
    emitIDUploadJourneyFailure,
    logIDUploadJourneyFailure: (_, __: StartIDUploadJourneyResult): void => {
      throw new Error('Not implemented');
    },
    logError: (_, __: ActorErrorParams): void => {
      throw new Error('Not implemented');
    },
  },
  actors: {
    initializeFraudAlloy: fromPromise<InitializingFraudAlloyResult>(
      async () => {
        throw new Error('Not implemented');
      }
    ),
    startIDUploadJourney: fromPromise<
      StartIDUploadJourneyResult,
      SendStartIDUploadJourneyParams
    >(async () => {
      throw new Error('Not implemented');
    }),
  },
}).createMachine({
  id: 'fraudAlloyMachine',
  context: {
    neuroIdSiteId: null,
    neuroUserId: null,
    iovationBlackboxId: null,
  },
  initial: 'initializingFraudAlloy',
  on: {
    SEND_INITIALIZE_FRAUD_ALLOY: {
      target: '.initializingFraudAlloy',
    },
  },
  states: {
    initializingFraudAlloy: {
      invoke: {
        src: 'initializeFraudAlloy',
        onDone: [
          {
            actions: [
              {
                type: 'assignFraudAlloyInitResponse',
                params: getInitializeFraudAlloyParams,
              },
            ],
            target: 'ready',
          },
        ],
        onError: [
          {
            actions: [
              {
                type: 'logError',
                params: getServiceErrorParams,
              },
            ],
            // Always go to ready
            target: 'ready',
          },
        ],
      },
    },
    ready: {
      entry: ['emitFraudAlloyMachineReady'],
      on: {
        SEND_START_ID_UPLOAD_JOURNEY: {
          target: '.startingIDUploadJourney',
        },
      },
      initial: 'initial',
      states: {
        initial: {},
        startingIDUploadJourney: {
          invoke: {
            src: 'startIDUploadJourney',
            input: getStartIDUploadJourneyInput,
            onDone: [
              {
                guard: {
                  type: 'isIDUploadJourneySuccess',
                  params: getStartIDUploadJourney,
                },
                actions: [
                  {
                    type: 'emitIDUploadJourneySuccess',
                  },
                ],
                target: 'success',
              },
              {
                actions: [
                  {
                    type: 'emitIDUploadJourneyFailure',
                  },
                  {
                    type: 'logIDUploadJourneyFailure',
                    params: getStartIDUploadJourney,
                  },
                ],
                target: 'failure',
              },
            ],
            onError: [
              {
                actions: [
                  {
                    type: 'logError',
                    params: getServiceErrorParams,
                  },
                  {
                    type: 'emitIDUploadJourneyFailure',
                  },
                ],
                target: 'failure',
              },
            ],
          },
        },
        success: {},
        failure: {},
      },
    },
  },
});
